"use client";

import { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const tiersBenefitsInputs = [
  {
    name: "general",
    type: "object",
    required: true,
    defaultValue: {
      name: "Unlock exclusive benefits",
      description:
        "Members of Ploom Club receive a personalised experience from start to finish. Plus you'll enjoy great benefits such as:",
      background:
        "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F85b5b2e824ff4791908ef53dfcc3318f",
    },
    subFields: [
      {
        name: "name",
        type: "string",
        required: true,
      },
      {
        name: "description",
        type: "richText",
        required: true,
      },
    ],
  },
  {
    name: "tier1",
    type: "object",
    required: true,
    defaultValue: {
      name: "Silver level",
      description: "0-600 points",
      asset:
        "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F3c58691092654ec2a10a388de05b820d",
    },
    subFields: [
      {
        name: "name",
        type: "string",
        required: true,
      },
      {
        name: "description",
        type: "string",
        required: true,
      },
      {
        name: "asset",
        type: "file",
        required: true,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "altText",
        friendlyName: "Alt text",
        type: "string",
        required: false,
      },
    ],
  },
  {
    name: "tier2",
    type: "object",
    required: true,
    defaultValue: {
      name: "Gold level",
      description: "600-1000 points",
      asset:
        "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F9c05524fdeff49e9816735df9505fc83",
    },
    subFields: [
      {
        name: "name",
        type: "string",
        required: true,
      },
      {
        name: "description",
        type: "string",
        required: true,
      },
      {
        name: "asset",
        type: "file",
        required: true,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "altText",
        friendlyName: "Alt text",
        type: "string",
        required: false,
      },
    ],
  },
  {
    name: "tier3",
    type: "object",
    required: true,
    defaultValue: {
      name: "Black level",
      description: "1000+ points",
      asset:
        "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fe85a3845bdba4ad48388004e645edfe2",
    },
    subFields: [
      {
        name: "name",
        type: "string",
        required: true,
      },
      {
        name: "description",
        type: "string",
        required: true,
      },
      {
        name: "asset",
        type: "file",
        required: true,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "altText",
        friendlyName: "Alt text",
        type: "string",
        required: false,
      },
    ],
  },
  {
    name: "benefitsSections",
    type: "list",
    required: true,
    subFields: [
      {
        name: "name",
        type: "string",
        required: true,
        defaultValue: "Benefits",
      },
      {
        name: "benefits",
        type: "list",
        required: true,
        subFields: [
          {
            name: "name",
            type: "string",
            required: true,
          },
          {
            name: "tier1State",
            friendlyName: "Tier 1 state",
            type: "string",
            enum: ["yes", "no", "other"],
            required: true,
          },
          {
            name: "tier1Label",
            friendlyName: "Tier 1 label",
            type: "string",
            required: false,
          },
          {
            name: "tier2State",
            friendlyName: "Tier 2 state",
            type: "string",
            enum: ["yes", "no", "other"],
            required: true,
          },
          {
            name: "tier2Label",
            friendlyName: "Tier 2 label",
            type: "string",
            required: false,
          },
          {
            name: "tier3State",
            friendlyName: "Tier 3 state",
            type: "string",
            enum: ["yes", "no", "other"],
            required: true,
          },
          {
            name: "tier3Label",
            friendlyName: "Tier 3 label",
            type: "string",
            required: false,
          },
          {
            name: "isHighLightened",
            friendlyName: "Highlight benefit",
            type: "boolean",
          },
        ],
      },
    ],
    defaultValue: [
      {
        name: "Exclusive Benefits",
        benefits: [
          {
            name: "Exclusive Offers",
            isHighLightened: null,
            tier1State: "yes",
            tier1Label: null,
            tier2State: "yes",
            tier2Label: null,
            tier3State: "yes",
            tier3Label: null,
          },
          {
            name: "Free shipping",
            isHighLightened: null,
            tier1State: "yes",
            tier1Label: null,
            tier2State: "yes",
            tier2Label: null,
            tier3State: "yes",
            tier3Label: null,
          },
          {
            name: "Personal Ploom Assistant",
            isHighLightened: false,
            tier1State: "yes",
            tier1Label: null,
            tier2State: "yes",
            tier2Label: null,
            tier3State: "yes",
            tier3Label: null,
          },
          {
            name: "14-day Free Trial",
            isHighLightened: false,
            tier1State: "yes",
            tier1Label: null,
            tier2State: "yes",
            tier2Label: null,
            tier3State: "yes",
            tier3Label: null,
          },
          {
            name: "Product pre-order",
            isHighLightened: false,
            tier1State: "other",
            tier1Label: "Limited",
            tier2State: "other",
            tier2Label: "Unlimited",
            tier3State: "other",
            tier3Label: "Unlimited",
          },
          {
            name: "Device Free Replacement",
            isHighLightened: false,
            tier1State: "no",
            tier1Label: null,
            tier2State: "yes",
            tier2Label: null,
            tier3State: "yes",
            tier3Label: null,
          },
          {
            name: "Exclusive Discounts",
            isHighLightened: null,
            tier1State: "no",
            tier1Label: null,
            tier2State: "other",
            tier2Label: "5%",
            tier3State: "other",
            tier3Label: "10%",
          },
          {
            name: "Invitation to Ploom Events",
            isHighLightened: null,
            tier1State: "no",
            tier1Label: null,
            tier2State: "other",
            tier2Label: "Limited",
            tier3State: "other",
            tier3Label: "Unlimited + 10%",
          },
          {
            name: "Ploom Instore Exclusives",
            isHighLightened: null,
            tier1State: "no",
            tier1Label: null,
            tier2State: "yes",
            tier2Label: null,
            tier3State: "yes",
            tier3Label: null,
          },
          {
            name: "Behind the Scenes Access",
            isHighLightened: null,
            tier1State: "no",
            tier1Label: null,
            tier2State: "yes",
            tier2Label: null,
            tier3State: "yes",
            tier3Label: null,
          },
          {
            name: "Device Upgrade / Replacement",
            isHighLightened: null,
            tier1State: "no",
            tier1Label: null,
            tier2State: "yes",
            tier2Label: null,
            tier3State: "other",
            tier3Label: "1 free",
          },
          {
            name: "Accessory Customization",
            isHighLightened: null,
            tier1State: "no",
            tier1Label: null,
            tier2State: "yes",
            tier2Label: null,
            tier3State: "yes",
            tier3Label: null,
          },
        ],
      },
    ],
  },
  {
    name: "componentId",
    friendlyName: "Component ID",
    type: "string",
    required: true,
    defaultValue: "tiers-benefits",
  },
] as const satisfies Inputs;

export const TiersBenefitsComponent: RegisteredComponent = {
  name: "TiersBenefits",
  component: dynamic(async () =>
    withValidation(
      tiersBenefitsInputs,
      (await import("./TiersBenefits")).TiersBenefits,
      "TiersBenefits"
    )
  ),
  inputs: tiersBenefitsInputs,
};
